/**
 * PhotoLightbox — full-screen modal for viewing a single vehicle photo.
 *
 * Reuses useModalFocus and the AttestModal overlay chrome pattern.
 * Escape key closes. ≥44px close button with aria-label "Close photo".
 *
 * Footer is a flex row (Slice-2 action slot reserved).
 * Swipe / arrow paging deferred to Slice 2.
 */

import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { useModalFocus } from '@/hooks/useModalFocus';
import { usePhotoImage } from '@/hooks/usePhotoImage';
import type { PhotoQueueItem } from '@/lib/photoQueue';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useEffect } from 'react';
import type { CommittedPhoto, PhotoTileItem } from './PhotoTile';

interface PhotoLightboxProps {
  item: PhotoTileItem | null;
  onClose: () => void;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return format(new Date(iso), 'd MMM yyyy');
  } catch {
    return '';
  }
}

// Inner: renders committed photo via authenticated fetch.
function CommittedLightboxImage({ photo }: { photo: CommittedPhoto }) {
  const imgState = usePhotoImage(photo.photoId, true);

  if (imgState.status === 'loading' || imgState.status === 'idle') {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: T.textMuted,
          fontSize: 13,
          fontFamily: F.body,
        }}
        role="status"
        aria-label="Loading photo"
      >
        Loading…
      </div>
    );
  }

  if (imgState.status === 'ready') {
    return (
      <img
        src={imgState.url}
        alt={photo.caption ?? 'Vehicle photo'}
        style={{
          flex: 1,
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    );
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: T.textMuted,
        fontSize: 13,
        fontFamily: F.body,
      }}
    >
      Preview unavailable
    </div>
  );
}

export function PhotoLightbox({ item, onClose }: PhotoLightboxProps) {
  const isOpen = item !== null;
  const { dialogRef, initialRef: closeRef } = useModalFocus<HTMLDivElement, HTMLButtonElement>(
    isOpen,
  );

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!item) return null;

  const isQueued = 'type' in item && item.type === 'queued';
  const queuedItem = isQueued ? (item as PhotoQueueItem & { type: 'queued' }) : null;
  const committedItem = !isQueued ? (item as CommittedPhoto) : null;

  const caption = committedItem?.caption ?? queuedItem?.caption;
  const capturedAt = committedItem?.capturedAt ?? queuedItem?.capturedAt;
  const displayDate = formatDate(capturedAt);

  const overlayStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.88)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const dialogStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    maxWidth: 900,
    maxHeight: '100vh',
    position: 'relative',
    background: T.bg,
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: `${SPACE.sm}px ${SPACE.sm}px ${SPACE.xs}px`,
    borderBottom: `1px solid ${T.border}`,
    flexShrink: 0,
  };

  const imageAreaStyle: CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: T.bg,
    overflow: 'hidden',
    padding: SPACE.sm,
    minHeight: 0,
  };

  const footerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.md,
    padding: `${SPACE.sm}px ${SPACE.md}px`,
    borderTop: `1px solid ${T.border}`,
    flexShrink: 0,
    minHeight: 56,
  };

  const closeBtnStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: T.text,
    borderRadius: RADIUS.md,
    padding: SPACE.xs,
    minWidth: 44,
    minHeight: 44,
    transition: `background ${TRANSITION.fast}`,
  };

  return (
    <div style={overlayStyle} role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Photo viewer"
        style={dialogStyle}
      >
        {/* Header with close button */}
        <div style={headerStyle}>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close photo"
            style={closeBtnStyle}
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Image area */}
        <div style={imageAreaStyle}>
          {committedItem ? (
            <CommittedLightboxImage photo={committedItem} />
          ) : queuedItem?.previewUrl ? (
            <img
              src={queuedItem.previewUrl}
              alt={caption ?? 'Vehicle photo'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <div style={{ color: T.textMuted, fontSize: 13, fontFamily: F.body }}>
              Preview unavailable
            </div>
          )}
        </div>

        {/* Footer — caption + date */}
        <div style={footerStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {caption ? (
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  fontFamily: F.body,
                  color: T.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {caption}
              </p>
            ) : null}
            {displayDate ? (
              <p
                style={{
                  margin: caption ? `${SPACE.xs}px 0 0` : 0,
                  fontSize: 12,
                  fontFamily: F.mono,
                  color: T.textMuted,
                }}
              >
                {displayDate}
              </p>
            ) : null}
            {!caption && !displayDate ? (
              <p style={{ margin: 0, fontSize: 13, fontFamily: F.body, color: T.textMuted }}>
                No caption
              </p>
            ) : null}
          </div>
          {/* RESERVED: Slice-2 action slot */}
        </div>
      </div>
    </div>
  );
}
